import { signal, computed, Signal } from '@angular/core'
import { BehaviorSubject, defer, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { createStoreDevTools } from '../../common/utils/store-devtools.util'
import { BaseEntityState } from './base.state'

export type EntityId = string | number

export class BaseStore<T extends BaseEntityState<E>, E extends { id: EntityId }> {
  private _state$!: BehaviorSubject<T>
  private _sig!: ReturnType<typeof signal<T>>
  readonly state$!: Observable<T>

  // ─── Selectores genéricos ─────────────────────────────────────────────────

  readonly entities$: Observable<E[]> = this.select((s) => s.entities)
  readonly isLoading$: Observable<boolean> = this.select((s) => s.isLoading)
  readonly error$: Observable<string | null> = this.select((s) => s.error)

  entitiesByIds$(ids: EntityId[]): Observable<E[]> {
    return this.select((s) => {
      if (!ids.length) return []
      const idSet = new Set(ids)
      return s.entities.filter((e) => idSet.has(e.id))
    })
  }

  entityById$(id: EntityId): Observable<E | undefined> {
    return this.select((s) => s.entities.find((e) => e.id === id))
  }

  // ─── Updaters comunes ─────────────────────────────────────────────────────

  readonly setLoading = this.updater<boolean>((state, loading) => ({
    ...state,
    isLoading: loading,
    error: loading ? null : state.error,
  }))

  readonly setError = this.updater<string | null>((state, error) => ({
    ...state,
    error,
    isLoading: false,
  }))

  readonly reset: () => void = () => {
    this._state$.next(this.initialState)
    this._sig.set(this.initialState)
  }

  // ─── API ─────────────────────────────────────────────────────────────────

  get(): T {
    return this._state$.getValue()
  }

  select<R>(projector: (state: T) => R): Observable<R> {
    if (!this._state$) {
      return defer(() => this._state$).pipe(map(projector), distinctUntilChanged())
    }
    return this._state$.pipe(map(projector), distinctUntilChanged())
  }

  selectSignal<R>(projector: (state: T) => R): Signal<R> {
    return computed(() => projector(this._sig()))
  }

  updater<V>(fn: (state: T, value: V) => T): (value: V) => void
  updater(fn: (state: T) => T): () => void
  updater<V>(fn: (state: T, value?: V) => T) {
    return (value?: V) => {
      const next = fn(this._state$.getValue(), value as V)
      this._state$.next(next)
      this._sig.set(next)
    }
  }

  effect<V = void>(generator: (source$: Observable<V>) => Observable<unknown>): (value: V) => void {
    const subject$ = new Subject<V>()
    generator(subject$.asObservable()).subscribe()
    return (value: V) => subject$.next(value)
  }

  protected setState(fn: (state: T) => T): void {
    const next = fn(this._state$.getValue())
    this._state$.next(next)
    this._sig.set(next)
  }

  protected patchState(partial: Partial<T>): void {
    const next = { ...this._state$.getValue(), ...partial }
    this._state$.next(next)
    this._sig.set(next)
  }

  // ─── Constructor ──────────────────────────────────────────────────────────

  protected constructor(
    protected readonly initialState: T,
    storeName = 'Store',
  ) {
    this._state$ = new BehaviorSubject<T>(initialState)
    ;(this as any).state$ = this._state$.asObservable()
    this._sig = signal<T>(initialState)

    const manualDebug = typeof window !== 'undefined' && window.localStorage?.getItem('enable_store_debug') === '1'

    if (environment.features.enableLogging && manualDebug) {
      console.log(`${storeName} initialized`)
    }

    const manualDevTools =
      typeof window !== 'undefined' && window.localStorage?.getItem('enable_store_devtools') === '1'

    if (environment.features.enableDevTools && manualDevTools) {
      const devTools = createStoreDevTools(storeName, true)
      devTools.initState(initialState)
      devTools.monitorState(this.state$).subscribe()
    }
  }
}
